import React from 'react';
import Breadcrumb from '@atlassian/jira-breadcrumbs/src/breadcrumb/component.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { useRouter } from '@atlassian/jira-router';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import CycleTimeReport from '@atlassian/jira-software-cycle-time-breakdown-report';
import {
	ViewCycleTimeReportExpectedErrorExperienceSuccessTracker,
	useViewCycleTimeReportExperienceFail,
	ViewCycleTimeReportExperienceStart,
} from '@atlassian/jira-software-cycle-time-breakdown-report/src/services/experience-tracker/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater';
import { AsyncNoSoftwareLicense } from '@atlassian/jira-spa-apps-common/src/error-screens/no-software-license/async';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication';
import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import messages from './messages';
import { PreconditionCheck } from './precondition';
import { isNetworkError } from './utils';

export const CycleTimeReportSpa = () => {
	const tenantContext = useTenantContext();
	const { formatMessage } = useIntl();
	const [{ match }] = useRouter();
	const projectKey = match.params.projectKey ?? '';
	const boardId = match.params.boardId ?? '';
	const { data: projectContext } = useProjectContext();

	const onViewCycleTimeReportExperienceFail = useViewCycleTimeReportExperienceFail();

	const hasSoftwareLicense = checkLicense(tenantContext, 'software');
	const reportsUrl = projectContext?.simplified
		? `${tenantContext.baseUrl}/jira/software/projects/${projectKey}/boards/${boardId}/reports`
		: `${tenantContext.baseUrl}/projects/${projectKey}?selectedItem=com.atlassian.jira.jira-projects-plugin:report-page`;

	return (
		<>
			<ViewCycleTimeReportExperienceStart experienceId={projectKey} />
			<AuthenticationCheck>
				{hasSoftwareLicense ? (
					<JSErrorBoundary
						id="pageContent"
						packageName="jiraSpaAppsSoftwareReportsCycleTimeReport"
						teamName="fusion-solaris"
						extraEventData={{
							isSimplifiedProject: projectContext?.simplified ?? false,
						}}
						onError={(location: string, error: Error) => {
							onViewCycleTimeReportExperienceFail(location, error, {
								isNetworkError: isNetworkError(error),
							});
						}}
						attributes={(error: Error) => ({
							isNetworkError: isNetworkError(error),
							isSimplifiedProject: projectContext?.simplified ?? false,
						})}
					>
						<SubProductUpdater subProduct="software" />
						<ProjectBreadcrumb
							baseUrl={tenantContext.baseUrl}
							projectKey={projectKey}
							projectName={projectContext?.projectName ?? ''}
						>
							<Breadcrumb
								breadcrumb={{
									text: formatMessage(messages.reports),
									href: reportsUrl,
								}}
							>
								<PreconditionCheck projectKey={toProjectKey(projectKey)}>
									{(hasProviders: boolean) => (
										<>
											<DocumentTitle title={formatMessage(messages.browserTitle)} />
											<CycleTimeReport hasProviders={hasProviders} />
										</>
									)}
								</PreconditionCheck>
							</Breadcrumb>
						</ProjectBreadcrumb>
					</JSErrorBoundary>
				) : (
					<>
						<AsyncNoSoftwareLicense />
						<ViewCycleTimeReportExpectedErrorExperienceSuccessTracker reason="No Jira Software license" />
					</>
				)}
			</AuthenticationCheck>
		</>
	);
};
